import {Injectable} from '@angular/core';
import {BehaviorSubject, timer, Observable } from 'rxjs';

export type FeedbackState =
  | { phase: 'hidden' }
  | { phase: 'loading'; text: string }
  | { phase: 'success'; text: string }
  | { phase: 'error'; text: string };

export interface ConfirmRequest {
  text: string;
  resolve: (ok: boolean) => void;
}

@Injectable({providedIn: 'root'})
export class ApiFeedbackService {
  private readonly defaultErrorMessage = 'Lo sentimos. Hubo un problema. Inténtalo de nuevo más tarde';
  private readonly technicalHttpErrorPattern = /^http failure (response|during parsing) for\s+/i;
  private readonly messagePriorityKeys = ['error', 'errors', 'detail', 'details', 'title', 'message', 'msg'] as const;
  private state$ = new BehaviorSubject<FeedbackState>({phase: 'hidden'});
  readonly feedback$ = this.state$.asObservable();
  private confirm$ = new BehaviorSubject<ConfirmRequest | null>(null);
  readonly onConfirm: Observable<ConfirmRequest | null> = this.confirm$.asObservable();
  private lastErrorAt = 0;

  showLoading(text = 'Cargando…') {
    this.state$.next({phase: 'loading', text});
  }

  showSuccess(text = 'Datos guardados con éxito') {
    this.state$.next({phase: 'success', text});
    timer(1500).subscribe(() => this.hide());
  }

  showError(text: unknown = this.defaultErrorMessage) {
    const safeText = this.sanitizeErrorMessage(text);
    const now = Date.now();
    const current = this.state$.value;
    if (current.phase === 'error' && current.text === safeText && (now - this.lastErrorAt) < 400) {
      return;
    }
    this.lastErrorAt = now;
    this.state$.next({phase: 'error', text: safeText});
    timer(3500).subscribe(() => this.hide());
  }

  private sanitizeErrorMessage(value: unknown): string {
    const normalized = this.extractErrorMessage(value);
    if (!normalized) {
      return this.defaultErrorMessage;
    }

    if (this.technicalHttpErrorPattern.test(normalized)) {
      return this.defaultErrorMessage;
    }

    return normalized;
  }

  private extractErrorMessage(value: unknown, depth = 0, visited = new Set<unknown>()): string | null {
    if (value == null || depth > 6) {
      return null;
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      return normalized.length > 0 ? normalized : null;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const message = this.extractErrorMessage(item, depth + 1, visited);
        if (message) {
          return message;
        }
      }
      return null;
    }

    if (typeof value !== 'object') {
      return null;
    }

    if (visited.has(value)) {
      return null;
    }
    visited.add(value);

    const record = value as Record<string, unknown>;

    for (const key of this.messagePriorityKeys) {
      if (!(key in record)) {
        continue;
      }
      const message = this.extractErrorMessage(record[key], depth + 1, visited);
      if (message) {
        return message;
      }
    }

    for (const rawValue of Object.values(record)) {
      const message = this.extractErrorMessage(rawValue, depth + 1, visited);
      if (message) {
        return message;
      }
    }

    return null;
  }

  hide() {
    this.state$.next({phase: 'hidden'});
  }

  hideLoading() {
    if (this.state$.value.phase === 'loading') {
      this.hide();
    }
  }

  confirm(text: string): Promise<boolean> {
    return new Promise(resolve => {
      this.confirm$.next({text, resolve});
    });
  }

  answerConfirm(ok: boolean) {
    const req = this.confirm$.value;
    if (req) {
      req.resolve(ok);
      this.confirm$.next(null);
    }
  }

}
